import CheckCircle from "@mui/icons-material/CheckCircle";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Alert, Divider, Stack, Typography } from "@mui/material";
import { BigTooltip } from "components/BigTooltip";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { getRegulatoryStatusDiscrepancies } from "encamp-shared/src/waste/index";
import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { useParams } from "react-router-dom";
import {
  FederalGeneratorStatusIndicator,
  FederalGeneratorStatusIndicatorProps,
} from "./FederalGeneratorStatusIndicator";
import { useWasteFacility } from "./useWasteFacility";

function GeneratorStatusRow({
  status,
  type,
}: {
  status: FederalGeneratorStatusIndicatorProps["status"];
  type?: FederalGeneratorStatusIndicatorProps["type"];
}) {
  return (
    <Stack direction="row" spacing={3}>
      {[
        RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator,
        RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator,
        RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator,
      ].map((rcraStatus) => (
        <FederalGeneratorStatusIndicator
          key={rcraStatus}
          status={rcraStatus}
          type={type}
          active={status === rcraStatus}
        />
      ))}
    </Stack>
  );
}

function GeneratorStatusInfoTooltip() {
  return (
    <Stack spacing={2} width="200px">
      <Typography variant="caption" fontWeight={700}>
        Generator Status
      </Typography>
      <Typography variant="caption">
        This shows the facility's generator status obtained or calculated from a
        variety of sources:
      </Typography>
      <Typography variant="caption">
        <Typography variant="caption" fontStyle="italic" component="span">
          Current
        </Typography>
        <br />
        <Typography variant="caption">
          Generator Status currently listed in RCRAInfo.
        </Typography>
      </Typography>
      <Typography variant="caption">
        <Typography variant="caption" fontStyle="italic" component="span">
          Calculated Vendor
        </Typography>
        <br />
        <Typography variant="caption">
          The highest generator status for a month within the current year,
          calculated from vendor manifests ingested by Encamp.
        </Typography>
      </Typography>
      <Typography variant="caption">
        <Typography variant="caption" fontStyle="italic" component="span">
          Calculated e‑Manifest
        </Typography>
        <br />
        <Typography variant="caption">
          The highest generator status for a month within the current year,
          calculated from e‑Manifest manifests ingested by Encamp.
        </Typography>
      </Typography>
      <Typography variant="caption">
        If the facility's calculated generator statuses differ from the current
        reported generator status, you may need to file an update notification
        with the EPA.
      </Typography>
      <Typography variant="caption">
        If a status is not highlighted, it has either not been reported or we do
        not have this data.
      </Typography>
    </Stack>
  );
}

export const GeneratorStatusTile = () => {
  return (
    <DashboardTile size={5}>
      <GeneratorStatusTileContent />
    </DashboardTile>
  );
};

const GeneratorStatusTileContent = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data } = useWasteFacility(facilityId);

  const regulatoryStatus = data.facility?.regulatoryStatus;

  if (!regulatoryStatus) {
    return null;
  }

  const rcraInfoStatus = regulatoryStatus?.rcraInfo;
  const vendorStatus = regulatoryStatus?.vendorHighestStatusThisYear;
  const eManifestStatus = regulatoryStatus?.eManifestHighestStatusThisYear;
  const discrepancies = getRegulatoryStatusDiscrepancies(regulatoryStatus);
  const hasStatusDiscrepancy = discrepancies.length > 0;
  const hasVendorDiscrepancy = discrepancies.some((d) =>
    d.includes("vendor calculated generator status.")
  );
  const hasEManifestDiscrepancy = discrepancies.some((d) =>
    d.includes("eManifest calculated generator status.")
  );
  const missingStatus = RcraInfoFederalWasteGeneratorStatus.Undetermined;
  const hasCombinedDiscrepancies =
    hasVendorDiscrepancy &&
    hasEManifestDiscrepancy &&
    // Both missing or neither missing
    ((vendorStatus === missingStatus && eManifestStatus === missingStatus) ||
      (vendorStatus !== missingStatus && eManifestStatus !== missingStatus));

  return (
    <>
      <DashboardTile.Header
        title="Generator Status"
        infoTooltip={<GeneratorStatusInfoTooltip />}
      >
        {!hasStatusDiscrepancy ? (
          <BigTooltip title="The statuses between what's calculated from ingested manifest data compared to RCRAInfo are the same and you likely do not need to file an update notification with the EPA.">
            <CheckCircle color="success" sx={{ cursor: "pointer" }} />
          </BigTooltip>
        ) : null}
      </DashboardTile.Header>
      <DashboardTile.ContentArea>
        <Stack spacing={2}>
          <Typography variant="caption" fontWeight={700}>
            Current Generator Status via RCRAInfo
          </Typography>

          <GeneratorStatusRow status={rcraInfoStatus} type="circle" />

          <Divider />

          <Stack spacing={1}>
            <Typography variant="caption">
              Calculated Vendor Generator Status YTD
            </Typography>

            <GeneratorStatusRow status={vendorStatus} />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="caption">
              Calculated e‑Manifest Generator Status YTD
            </Typography>

            <GeneratorStatusRow status={eManifestStatus} />
          </Stack>

          {hasStatusDiscrepancy && (
            <>
              {/* Show combined message if both have discrepancies */}
              {hasCombinedDiscrepancies ? (
                <Alert severity="warning" icon={<WarningAmber />}>
                  <Typography variant="caption">
                    {vendorStatus ===
                    RcraInfoFederalWasteGeneratorStatus.Undetermined
                      ? "Vendor and e‑Manifest manifest data missing."
                      : `Based on your vendor and e‑Manifest data, your calculated
                    generator status is different from what is reported in
                    RCRAInfo. You may need to file an update notification with
                    the EPA.`}
                  </Typography>
                </Alert>
              ) : (
                // Otherwise show individual messages as needed
                <>
                  {hasVendorDiscrepancy && (
                    <Alert severity="warning" icon={<WarningAmber />}>
                      <Typography variant="caption">
                        {vendorStatus ===
                        RcraInfoFederalWasteGeneratorStatus.Undetermined
                          ? "Vendor manifest data missing."
                          : `Based on your vendor manifest data, your calculated
                    generator status is different from what is reported in
                    RCRAInfo. You may need to file an update notification with
                    the EPA.`}
                      </Typography>
                    </Alert>
                  )}
                  {hasEManifestDiscrepancy && (
                    <Alert severity="warning" icon={<WarningAmber />}>
                      <Typography variant="caption">
                        {eManifestStatus ===
                        RcraInfoFederalWasteGeneratorStatus.Undetermined
                          ? "e‑Manifest manifest data missing."
                          : `Based on your e‑Manifest data, your calculated generator
                    status is different from what is reported in RCRAInfo. You
                    may need to file an update notification with the EPA.`}
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </DashboardTile.ContentArea>
    </>
  );
};
